import React, { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  BaseTable,
  Icons,
  Table as ShellTable,
  Sizes,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  THead,
  TBody,
  TH as Th,
  TD as Td,
  TR as Tr,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/components/TableElements/TableElements';
import styled from 'styled-components';
import Button from 'src/UI/Button';
import { Modal } from '../style';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { IPosition } from 'src/types/EUMarkets.type';

const PositionTable = styled(ShellTable)`
  .parent-row-expanded > td {
    border-bottom: none;
  }

  .subtable-row td {
    padding-top: 0;
    padding-bottom: 10px;
  }

  .subtable-wrapper {
    width: 75%;
    margin: 0 auto;
    padding-top: 6px;
    max-height: 260px;
    overflow-y: auto;
    border-bottom: 1px solid ${(props) => props?.theme?.border?.medium || '#5d5d5d'};
  }
`;

const PositionScrollArea = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

interface IViewPositionsProps {
  open: boolean;
  onClose: () => void;
  positions: IPosition[];
  contractName: string;
}

const { ChevronRightSquare, ChevronDownSquare } = Icons;

const ViewPositions: FC<IViewPositionsProps> = ({ open, onClose, positions, contractName }) => {
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (open) {
      setExpandedRows({});
    }
  }, [open]);

  const toggleExpandedRow = (positionId: number) => {
    setExpandedRows((prevState) => ({
      ...prevState,
      [positionId]: !prevState[positionId],
    }));
  };

  const renderDate = (dateValue?: string | null) => {
    return dateValue ? dayjs(dateValue).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : 'No Expiry';
  };

  const renderParamValueByType = (item) => {
    if (item.valueBoolean !== null) {
      return item.valueBoolean.toString();
    }
    if (item.valueDouble !== null) {
      return item.valueDouble.toString();
    }
    if (item.valueLong !== null) {
      return item.valueLong.toString();
    }
    if (item.valueString !== null) {
      return item.valueString;
    }
    return '';
  };

  const parameterColumns = [
    {
      accessorKey: 'parameterName',
      header: 'Parameter Name',
    },
    {
      accessorKey: 'value',
      header: 'Parameter Value',
      cell: ({ row }) => renderParamValueByType(row.original),
    },
    {
      accessorKey: 'validFrom',
      header: 'Valid From',
      cell: ({ getValue }) => renderDate(getValue()),
    },
    {
      accessorKey: 'validTo',
      header: 'Valid To',
      cell: ({ getValue }) => renderDate(getValue()),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      maskClosable={false}
      isDraggable
      width={1200}
      footer={
        <div className='button-style'>
          <Button variant='outlined' onClick={onClose} data-testid='close'>
            Close
          </Button>
        </div>
      }
      title={`Positions for contract: ${contractName}`}
    >
      <PositionScrollArea>
        <PositionTable className='shell-table' data-testid='positions-table' size='small'>
          <THead>
            <Tr>
              <Th>Position Name</Th>
              <Th>Nomination Group</Th>
              <Th>Location</Th>
              <Th>Direction</Th>
              <Th>Balances</Th>
              <Th>Valid From</Th>
              <Th>Valid To</Th>
            </Tr>
          </THead>
          <TBody>
            {positions.map((position) => {
              const isExpanded = Boolean(expandedRows[position.id]);

              return (
                <React.Fragment key={position.id}>
                  <Tr className={isExpanded ? 'parent-row-expanded' : ''}>
                    <Td>
                      <Button
                        onClick={() => toggleExpandedRow(position.id)}
                        size={Sizes.Small}
                        icon={isExpanded ? <ChevronDownSquare /> : <ChevronRightSquare />}
                        variant='transparent'
                        className='btn-collapse-expand'
                        data-testid={`toggle-position-${position.id}`}
                      >
                        {position.positionName}
                      </Button>
                    </Td>
                    <Td>{position.nominationGroupName}</Td>
                    <Td>{position.locationName || '-'}</Td>
                    <Td>{position.directionName || '-'}</Td>
                    <Td>{position.balanceNames || '-'}</Td>
                    <Td>{renderDate(position.validFrom)}</Td>
                    <Td>{renderDate(position.validTo)}</Td>
                    <Td>{renderDate(position.validTo)}</Td>
                  </Tr>
                  {isExpanded && (
                    <Tr className='subtable-row'>
                      <Td colSpan={7} data-testid={`subtable-position-${position.id}`}>
                        <div className='subtable-wrapper'>
                          <BaseTable columns={parameterColumns} data={position.parameters ?? []} />
                        </div>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
            {!positions.length && (
              <Tr>
                <Td colSpan={7} className='no-data-cell'>
                  <div className='title_wrapper'>
                    <h3>No data found</h3>
                  </div>
                </Td>
              </Tr>
            )}
          </TBody>
        </PositionTable>
      </PositionScrollArea>
    </Modal>
  );
};

export default ViewPositions;
